
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { SPECIAL_ADMIN_EMAILS } from "./constants";

export const useAuthInitialization = ({
  setSession,
  setUser,
  setLoading,
  setAuthInitialized,
  checkAndUpdateUserRoles
}) => {
  useEffect(() => {
    console.log("AuthProvider initializing");
    let authSubscription: { unsubscribe: () => void } | null = null;
    let initializationTimeout: number | null = null;
    
    const initialize = async () => {
      try {
        initializationTimeout = window.setTimeout(() => {
          console.log("Auth initialization timed out, setting loading to false");
          setLoading(false);
          setAuthInitialized(true);
        }, 10000);
        
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
          async (event, newSession) => {
            console.log("Auth state changed:", event);
            
            if (event === 'SIGNED_OUT') {
              setSession(null);
              setUser(null);
              setLoading(false);
            } else if (newSession?.user) {
              setSession(newSession);
              setUser(newSession.user);
              
              const email = newSession.user.email?.toLowerCase();
              console.log("User authenticated:", email);
              
              if (email && SPECIAL_ADMIN_EMAILS.includes(email)) {
                console.log("✅ Special admin email detected:", email);
                toast.success(`Welcome back, admin ${email.split('@')[0]}`);
              }
              
              await checkAndUpdateUserRoles(newSession.user);
              setLoading(false);
            }
          }
        );
        
        authSubscription = subscription;

        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          throw error;
        }
        
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          const email = currentSession.user.email?.toLowerCase();
          console.log("Initial session loaded:", email);
          
          if (email && SPECIAL_ADMIN_EMAILS.includes(email)) {
            console.log("✅ Special admin email detected during initialization:", email);
          }
          
          await checkAndUpdateUserRoles(currentSession.user);
        }
        
        if (initializationTimeout) {
          window.clearTimeout(initializationTimeout);
          initializationTimeout = null;
        }
        
      } catch (error) {
        console.error("Auth initialization error:", error);
      } finally {
        setLoading(false);
        setAuthInitialized(true);
      }
    };

    initialize();

    return () => {
      if (authSubscription) {
        authSubscription.unsubscribe();
      }
      
      if (initializationTimeout) {
        window.clearTimeout(initializationTimeout);
      }
    };
  }, [setSession, setUser, setLoading, setAuthInitialized, checkAndUpdateUserRoles]);
};
